"use client";

import { useEffect, useState, useRef, useMemo } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import logo from "@/imports/Rann-Utsav-Logo-scaled_cr.png";
import Link from "next/link";
import Image from "next/image";
import { useAppDispatch, useAppSelector } from "@/hooks/hooks";
import {
  getAllActiveBanner,
  getAllActiveSettings,
  getAllCMSPageData,
  getHeaderMenuData,
} from "@/services/home.services";
import { usePathname } from "next/navigation";
import { ISettings } from "@/types/home.types";

const fallbackMenuItems = [
  { title: "Home", href: "/" },
  { title: "Dhordo Tent City", submenu: [] },
  { title: "Dholavira Tent City", submenu: [] },
  // { title: "Rann Packages", submenu: [] },
  { title: "Special Dates", submenu: [], href: "/special-dates/" },
  // { title: "Kutch Hotels", submenu: [] },
  { title: "Gallery", submenu: [], href: "/gallery/" },
  { title: "About Us", href: "/about-us/" },
  { title: "Contact", href: "/contact/" },
];

export default function Navigation() {
  const dispatch = useAppDispatch();
  const location = usePathname();
  const { SettingList, BannerList, HeaderMenuList } = useAppSelector(
    (state) => state.home,
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const hasDispatched = useRef(false);

  const Settings: Record<string, ISettings> = useMemo(() => {
    return (
      SettingList?.reduce(
        (acc, item) => {
          acc[item?.SettingKey] = item;
          return acc;
        },
        {} as Record<string, ISettings>,
      ) || {}
    );
  }, [SettingList]);

  const RannUtsavDate = Settings?.["RannUtsavDate"]?.SettingValue || "";

  useEffect(() => {
    if (hasDispatched.current) return;
    hasDispatched.current = true;

    dispatch(getAllCMSPageData(""));

    const shouldDispatchBanner = !BannerList || BannerList.length === 0;
    const shouldDispatchSetting = !SettingList || SettingList.length === 0;
    const shouldDispatchHeaderMenu = !HeaderMenuList;

    if (shouldDispatchBanner) {
      dispatch(getAllActiveBanner());
    }
    if (shouldDispatchSetting) {
      dispatch(getAllActiveSettings());
    }
    if (shouldDispatchHeaderMenu) {
      dispatch(getHeaderMenuData());
    }
  }, [dispatch, BannerList, SettingList, HeaderMenuList]);

  // Dynamically compile menu items from HeaderMenuList
  const activeMenuItems = useMemo(() => {
    if (!HeaderMenuList) return fallbackMenuItems;

    const items: Array<{
      title: string;
      href?: string;
      submenu?: Array<{ title: string; href: string }>;
    }> = [{ title: "Home", href: "/" }];

    // 1. Dhordo Tent City
    const dhordoSub = (HeaderMenuList.DhordoTentCity || []).map((item) => ({
      title: item.Title,
      href: `/resort/${item.URL}/`,
    }));
    items.push({
      title: "Dhordo Tent City",
      submenu: dhordoSub,
    });

    // 2. Dholavira Tent City
    const dholaviraSub = (HeaderMenuList.DholaviraTentCity || []).map(
      (item) => ({
        title: item.Title,
        href: `/resort/${item.URL}/`,
      }),
    );
    items.push({
      title: "Dholavira Tent City",
      submenu: dholaviraSub,
    });

    //Special Dates
    items.push({
      title: "Special Dates",
      href: "/special-dates/",
    });

    //Gallery
    items.push({
      title: "Gallery",
      href: "/gallery/",
    });

    // 6. About Us
    items.push({
      title: "About Us",
      href: "/about-us/",
    });

    // 7. Contact
    items.push({
      title: "Contact",
      href: "/contact/",
    });

    return items;
  }, [HeaderMenuList]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-md">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link href="/" className="relative transform -translate-y-1">
            <Image
              src={logo}
              alt="Rann Utsav"
              priority
              className="h-12 md:h-14 w-auto cursor-pointer"
            />
            <span className="absolute -bottom-3 right-0 text-[12.5px] md:text-[14.5px] font-bold text-pink-600 tracking-wider whitespace-nowrap mt-0.5">
              {RannUtsavDate}
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-6">
            {activeMenuItems.map((item, index) => (
              <div
                key={index}
                className="relative group"
                onMouseEnter={() => {
                  if (item.submenu && item.submenu.length > 0) {
                    setOpenSubmenu(item.title);
                  }
                }}
                onMouseLeave={() => {
                  if (item.submenu && item.submenu.length > 0) {
                    setOpenSubmenu(null);
                  }
                }}
              >
                {item.submenu && item.submenu.length > 0 ? (
                  <>
                    <button
                      className={`flex items-center gap-1 py-2 text-sm font-medium text-gray-700 hover:text-pink-600 transition whitespace-nowrap cursor-pointer ${item.submenu.length > 0 && item.submenu?.map((subitem) => subitem.href).includes(location) ? "text-pink-600" : ""}`}
                    >
                      {item.title}
                      <ChevronDown className="w-3 h-3" />
                    </button>
                    {openSubmenu === item.title && (
                      <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-gray-100 z-50 before:content-[''] before:absolute before:-top-3 before:left-0 before:right-0 before:h-3 before:bg-transparent">
                        {item.submenu.map((subitem, subindex) => (
                          <Link
                            key={subindex}
                            href={subitem.href}
                            className="block px-6 py-2 text-sm font-medium text-gray-700 hover:bg-gradient-to-r hover:from-pink-50 hover:to-purple-50 transition"
                            onClick={() => setOpenSubmenu(null)}
                          >
                            {subitem.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : item.href ? (
                  <Link
                    href={item.href}
                    className={`py-2 text-sm font-medium text-gray-700 hover:text-pink-600 transition whitespace-nowrap ${location === item.href ? "text-pink-600" : ""}`}
                  >
                    {item.title}
                  </Link>
                ) : (
                  <span className="py-2 text-sm font-medium text-gray-700 hover:text-pink-600 transition whitespace-nowrap cursor-default">
                    {item.title}
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden cursor-pointer p-3 w-12 h-12 flex items-center justify-center -mr-3"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? "Close main menu" : "Open main menu"}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            {activeMenuItems.map((item, index) => (
              <div key={index}>
                {item.submenu && item.submenu.length > 0 ? (
                  <>
                    <button
                      onClick={() =>
                        setOpenSubmenu(
                          openSubmenu === item.title ? null : item.title,
                        )
                      }
                      className="w-full flex items-center justify-between py-3 text-base font-medium text-gray-700 hover:text-pink-600 transition cursor-pointer"
                    >
                      {item.title}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform ${openSubmenu === item.title ? "rotate-180" : ""}`}
                      />
                    </button>
                    {openSubmenu === item.title && (
                      <div className="pl-4 pb-2">
                        {item.submenu.map((subitem, subindex) => (
                          <Link
                            key={subindex}
                            href={subitem.href}
                            className="block py-2 text-sm font-medium text-gray-600 hover:text-pink-600 transition"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {subitem.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </>
                ) : item.href ? (
                  <Link
                    href={item.href}
                    className="block py-3 text-base font-medium text-gray-700 hover:text-pink-600 transition"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.title}
                  </Link>
                ) : (
                  <span className="block py-3 text-base font-medium text-gray-500 cursor-default">
                    {item.title}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
}
